import React, { useEffect, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import TabsWidget from 'cms/pages/shared/dashboard/widgets/TabsWidget';

/** Arabic / Syriac / etc. — editor should be RTL */
function usesRtlScript(name) {
  return name ? /[\u0600-\u06FF\u0700-\u074F]/.test(name) : false;
}

/** Latin Kurmanji (Kurdî) or Sorani — use Kurdish UI font where helpful */
function prefersKurdishFont(name) {
  if (!name) return false;
  const n = name.toLowerCase();
  return usesRtlScript(name) || n.includes('kurd');
}

/**
 * Language tabs (cms-level2-tabs) + title input + optional description editor.
 * Updates are immutable; parent receives the full `names` array via onNamesChange.
 */
export default function NamesWidget({
  languages,
  names,
  onNamesChange,
  titleField,
  descriptionField,
  stubTitleEntry,
  stubDescriptionEntry,
  allowNewEntryFromDescription = false,
  showDescription = true,
  /** Called when the user selects a different language tab (key = language id) */
  onActiveLanguageChange,
}) {
  const [activeLanguage, setActiveLanguage] = useState(null);

  useEffect(() => {
    if (!languages?.length) return;
    const ids = new Set(languages.map((l) => l.id));
    setActiveLanguage((prev) => {
      if (prev != null && ids.has(prev)) return prev;
      return languages.find((l) => l.is_default)?.id ?? languages[0].id;
    });
  }, [languages]);

  const getRow = (langId) => names?.find((n) => n.language === langId);

  const setTitle = (languageId, value) => {
    const lid = parseInt(languageId, 10);
    const next = names ? [...names] : [];
    const i = next.findIndex((n) => n.language === lid);
    if (i >= 0) {
      next[i] = { ...next[i], [titleField]: value };
    } else {
      next.push(stubTitleEntry(lid, value));
    }
    onNamesChange(next);
  };

  const setDescription = (languageId, data) => {
    const lid = parseInt(languageId, 10);
    const next = names ? [...names] : [];
    const i = next.findIndex((n) => n.language === lid);
    if (i >= 0) {
      next[i] = { ...next[i], [descriptionField]: data };
    } else if (allowNewEntryFromDescription && stubDescriptionEntry) {
      next.push(stubDescriptionEntry(lid, data));
    } else {
      return;
    }
    onNamesChange(next);
  };

  const handleTabChange = (key) => {
    setActiveLanguage(key);
    onActiveLanguageChange?.(key);
  };

  if (!languages?.length) {
    return null;
  }

  return (
    <div className={showDescription ? 'mb-10 mt-5p' : 'names-widget-title-only'}>
      <TabsWidget
        tabs={languages.map((l) => ({ key: l.id, label: l.name, icon: 'fa-globe' }))}
        activeTab={activeLanguage}
        onTabChange={handleTabChange}
        className="cms-level2-tabs"
      />
      <div className="cms-level2-tab-content">
        {languages.map(
          (language) =>
            activeLanguage === language.id && (
              <div key={language.id}>
                <div className="form-row">
                  <input
                    type="text"
                    name={titleField}
                    className={`${showDescription ? 'form-control mb-10 mxw-200' : 'form-control names-widget-title-input'}${prefersKurdishFont(language.name) ? ' kfont' : ''}`}
                    dir={usesRtlScript(language.name) ? 'rtl' : undefined}
                    style={{ fontSize: '16px' }}
                    value={getRow(language.id)?.[titleField] ?? ''}
                    onChange={(e) => setTitle(language.id, e.target.value)}
                    placeholder={language.name}
                  />
                </div>
                {showDescription && (
                  <div
                    className={`form-row${prefersKurdishFont(language.name) ? ' kfont' : ''}${usesRtlScript(language.name) ? ' rtl' : ''}${prefersKurdishFont(language.name) && !usesRtlScript(language.name) ? ' cms-editor-ltr' : ''}`}
                  >
                    <CKEditor
                      editor={ClassicEditor}
                      data={getRow(language.id)?.[descriptionField] ?? ''}
                      onReady={() => {}}
                      onChange={(event, editor) => {
                        setDescription(language.id, editor.getData());
                      }}
                    />
                  </div>
                )}
              </div>
            )
        )}
      </div>
    </div>
  );
}
